// Field-schema validation and value coercion. Pure — no I/O, no AWS.
//
// First consumer is activity-type CRUD (Session 2). Currency is stored as
// integer minor units (cents) so later cost rollups sum exactly (H-7).

import {
  FIELD_KINDS,
  type ActivityField,
  type FieldKind,
  type FieldValue,
} from "./types.js";

const FIELD_ID_RE = /^[a-z0-9]+(-[a-z0-9]+)*$/;
const DURATION_UNITS = new Set(["min", "hr"]);
const ISO_CURRENCY_RE = /^[A-Z]{3}$/;

const MAX_LABEL = 40;
const MAX_ENUM_OPTIONS = 20;
const MAX_FIELDS = 12;

export type CoerceResult =
  | { ok: true; value: FieldValue }
  | { ok: false; error: string };

export type CoerceValuesResult =
  | { ok: true; values: Record<string, FieldValue> }
  | { ok: false; errors: Record<string, string> };

function isFieldKind(value: unknown): value is FieldKind {
  return typeof value === "string" && (FIELD_KINDS as readonly string[]).includes(value);
}

function asString(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

function asBoolean(value: unknown): boolean | undefined {
  return typeof value === "boolean" ? value : undefined;
}

function asNumber(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

/** Per-field definition checks. Uniqueness across a type is validateFields(). */
export function validateFieldDefinition(field: unknown): string[] {
  const errors: string[] = [];
  if (field === null || typeof field !== "object" || Array.isArray(field)) {
    return ["field must be an object"];
  }
  const f = field as Record<string, unknown>;

  const fieldId = asString(f.fieldId);
  if (!fieldId) errors.push("fieldId is required");
  else if (!FIELD_ID_RE.test(fieldId)) {
    errors.push("fieldId must be kebab-case (lowercase letters, digits, hyphen-separated)");
  }

  const label = asString(f.label);
  if (!label || label.length < 1 || label.length > MAX_LABEL) {
    errors.push(`label must be 1-${MAX_LABEL} characters`);
  }

  if (!isFieldKind(f.kind)) {
    errors.push(`kind must be one of: ${FIELD_KINDS.join(", ")}`);
  }
  const kind = isFieldKind(f.kind) ? f.kind : undefined;

  if (asBoolean(f.required) === undefined) {
    errors.push("required must be a boolean");
  }

  const order = asNumber(f.order);
  if (order === undefined || !Number.isInteger(order) || order < 0) {
    errors.push("order must be a non-negative integer");
  }

  if (kind === "enum") {
    const options = f.options;
    if (!Array.isArray(options) || options.length < 1 || options.length > MAX_ENUM_OPTIONS) {
      errors.push(`enum requires 1-${MAX_ENUM_OPTIONS} options`);
    } else {
      for (const [i, opt] of options.entries()) {
        if (typeof opt !== "string" || opt.length < 1 || opt.length > MAX_LABEL) {
          errors.push(`options[${i}] must be 1-${MAX_LABEL} characters`);
        }
      }
    }
  } else if (f.options !== undefined) {
    errors.push("options is only valid on enum fields");
  }

  if (kind === "number" || kind === "duration" || kind === "currency") {
    const unit = asString(f.unit)?.trim();
    if (!unit) errors.push(`${kind} requires a unit`);
    else if (kind === "duration" && !DURATION_UNITS.has(unit)) {
      errors.push('duration unit must be "min" or "hr"');
    } else if (kind === "currency" && !ISO_CURRENCY_RE.test(unit)) {
      errors.push("currency unit must be a 3-letter ISO code (e.g. USD)");
    }
  }

  return errors;
}

export function validateFields(fields: unknown): string[] {
  if (!Array.isArray(fields)) return ["fields must be an array"];
  if (fields.length > MAX_FIELDS) return [`fields supports at most ${MAX_FIELDS} entries`];

  const errors: string[] = [];
  const seen = new Set<string>();
  for (const [i, field] of fields.entries()) {
    const fieldErrors = validateFieldDefinition(field);
    for (const err of fieldErrors) errors.push(`fields[${i}]: ${err}`);
    if (field && typeof field === "object" && !Array.isArray(field)) {
      const id = asString((field as Record<string, unknown>).fieldId);
      if (id) {
        if (seen.has(id)) errors.push(`fields[${i}]: fieldId "${id}" is not unique`);
        seen.add(id);
      }
    }
  }
  return errors;
}

function parseFiniteNumber(raw: unknown): number | undefined {
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return undefined;
    const n = Number(trimmed);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

function isBlank(raw: unknown): boolean {
  return raw === undefined || raw === null || (typeof raw === "string" && raw.trim() === "");
}

/**
 * Coerce a single raw value against one field definition.
 *
 * Currency: a string/number with a fractional part (or a decimal point in the
 * string) is treated as major units and converted to integer minor units;
 * an integer is already minor units. Duration is an integer count of `unit`.
 */
export function coerceFieldValue(field: ActivityField, raw: unknown): CoerceResult {
  if (isBlank(raw)) {
    if (field.required) return { ok: false, error: "this field is required" };
    return { ok: false, error: "empty" };
  }

  switch (field.kind) {
    case "text": {
      if (typeof raw !== "string") return { ok: false, error: "must be text" };
      const text = raw.trim();
      if (!text) {
        if (field.required) return { ok: false, error: "this field is required" };
        return { ok: false, error: "empty" };
      }
      return { ok: true, value: text };
    }
    case "boolean": {
      if (raw === true || raw === false) return { ok: true, value: raw };
      if (typeof raw === "string") {
        const lowered = raw.trim().toLowerCase();
        if (lowered === "true") return { ok: true, value: true };
        if (lowered === "false") return { ok: true, value: false };
      }
      return { ok: false, error: "must be true or false" };
    }
    case "enum": {
      if (typeof raw !== "string") return { ok: false, error: "must be one of the listed options" };
      const options = field.options ?? [];
      if (!options.includes(raw)) return { ok: false, error: "must be one of the listed options" };
      return { ok: true, value: raw };
    }
    case "number": {
      const n = parseFiniteNumber(raw);
      if (n === undefined) return { ok: false, error: "must be a number" };
      return { ok: true, value: n };
    }
    case "duration": {
      const n = parseFiniteNumber(raw);
      if (n === undefined || !Number.isInteger(n) || n < 0) {
        return { ok: false, error: "must be a whole number of " + (field.unit ?? "units") };
      }
      return { ok: true, value: n };
    }
    case "currency": {
      const minor = toMinorUnits(raw);
      if (minor === undefined) return { ok: false, error: "must be a currency amount" };
      if (minor < 0) return { ok: false, error: "must be zero or more" };
      return { ok: true, value: minor };
    }
    default: {
      const _never: never = field.kind;
      return { ok: false, error: `unsupported kind ${_never}` };
    }
  }
}

/**
 * Integer minor units (cents). A decimal string/number is major units;
 * an integer number or a digits-only string is already minor units.
 */
function toMinorUnits(raw: unknown): number | undefined {
  if (typeof raw === "number") {
    if (!Number.isFinite(raw)) return undefined;
    if (Number.isInteger(raw)) return raw;
    return Math.round(raw * 100);
  }
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return undefined;
    const n = Number(trimmed);
    if (!Number.isFinite(n)) return undefined;
    if (trimmed.includes(".")) return Math.round(n * 100);
    if (!Number.isInteger(n)) return Math.round(n * 100);
    return n;
  }
  return undefined;
}

/**
 * Coerce a values map against a type's field schema.
 *
 * Unknown fieldIds are dropped silently (a schema can shrink; old logs keep
 * their data but new writes must not invent fields). Missing required fields
 * produce a per-field error map. Empty optional values are omitted so
 * "absent key === not recorded".
 */
export function coerceValues(
  fields: ActivityField[],
  raw: Record<string, unknown> | undefined | null
): CoerceValuesResult {
  const input = raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {};
  const values: Record<string, FieldValue> = {};
  const errors: Record<string, string> = {};

  for (const field of fields) {
    const result = coerceFieldValue(field, input[field.fieldId]);
    if (result.ok) {
      values[field.fieldId] = result.value;
    } else if (result.error === "empty") {
      // optional + blank -> omit
    } else {
      errors[field.fieldId] = result.error;
    }
  }

  // Unknown keys on `input` are intentionally not copied and not reported.

  if (Object.keys(errors).length > 0) return { ok: false, errors };
  return { ok: true, values };
}
